import { AppBar, Box, Dialog, IconButton, Paper, Slide, Toolbar } from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { TransitionProps } from "@mui/material/transitions";
import { forwardRef, ReactElement, ReactNode, Ref } from "react";

// gutter is how far a sheet holds its fields off the edges, in px. What runs the
// full width lines its own text up with them.
export const gutter = 16;

const SlideUp = forwardRef(function SlideUp(
    props: TransitionProps & { children: ReactElement },
    ref: Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// MobileSheet is a page that comes up over the one a phone is on: it fills the
// screen, names itself in a bar with the way back, and lays its content out as
// cards on the grey canvas the app works on (see App).
export function MobileSheet(props: {
    // title is a node rather than text, so a sheet can put its own status beside
    // what it calls itself
    title: ReactNode;
    actions?: ReactNode;
    onClose: () => void;
    children: ReactNode;
}) {
    return (
        <Dialog fullScreen open onClose={props.onClose} slots={{ transition: SlideUp }}
                slotProps={{ paper: { sx: { bgcolor: "background.default" } } }}>
            <AppBar position="sticky" elevation={0}>
                <Toolbar variant="dense">
                    <IconButton color="inherit" edge="start" onClick={props.onClose} aria-label="Back">
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0, ml: 1 }}>
                        {props.title}
                    </Box>
                    {props.actions}
                </Toolbar>
            </AppBar>

            <Box sx={{
                display: "flex", flexDirection: "column", gap: 1.5, overflow: "auto",
                pt: 1.5, pb: "calc(12px + env(safe-area-inset-bottom))",
            }}>
                {props.children}
            </Box>
        </Dialog>
    );
}

// MobileCard is one of the white surfaces a sheet's content sits on. It runs to
// both edges, so only its top and bottom rules show.
export function MobileCard(props: {
    // inset holds the card's content off the edges, for content that isn't meant to
    // run to them the way a list or an editor does
    inset?: boolean;
    children: ReactNode;
}) {
    return (
        <Paper variant="outlined" square sx={{
            borderLeft: 0,
            borderRight: 0,
            ...(props.inset && { px: `${gutter}px`, py: 2 }),
        }}>
            {props.children}
        </Paper>
    );
}
